// frontend/src/pages/About.js
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

export default function About() {
  const navigate = useNavigate();

  return (
    <motion.div
      className="min-h-screen bg-blue-50 dark:bg-black pb-10 md:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      {/* Hero */}
      <header className="bg-white dark:bg-black shadow-lg border-b-4 border-blue-600">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-6 md:py-16 text-center">
          <h1 className="text-2xl sm:text-3xl md:text-5xl font-bold text-blue-800 dark:text-white mb-1.5 md:mb-4">
            About Coinzetto
          </h1>
          <p className="text-xs sm:text-sm md:text-xl text-blue-600 dark:text-gray-300 max-w-lg md:max-w-2xl mx-auto">
            Simple, secure, and transparent crypto trading—built by traders, for traders.
          </p>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 mt-5 md:mt-12 space-y-4 md:space-y-8">
        {/* Our Story */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-2.5 md:mb-4">
            Our Story
          </h2>
          <div className="space-y-2.5 md:space-y-4 text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 leading-[1.55] md:leading-relaxed">
            <p>
              For over five years, our founders have been active P2P cryptocurrency traders. They experienced firsthand how
              banking restrictions and account freezes could block their ability to sell and withdraw funds—sometimes for days or weeks.
            </p>
            <p>
              Frustrated by the lack of reliable, compliant rails between crypto and fiat, they envisioned a platform where traders
              never face those roadblocks—where deposits, trades, and withdrawals happen seamlessly on secure, regulated channels.
            </p>
            <p>
              In May 2025, after five years honing their trading strategies and navigating complex regulations, they officially
              registered Coinzetto as a company. Over the next months, they obtained the necessary licenses, built out the HD-wallet
              infrastructure, and integrated multiple blockchains. Now, with registrations complete and security audits passed,
              Coinzetto is live—ready to empower you to trade with confidence.
            </p>
          </div>
        </section>

        {/* Mission & Vision */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-8">
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-2 md:mb-3">Our Mission</h2>
              <p className="text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 leading-[1.55] md:leading-relaxed">
                To remove the friction between crypto and fiat by providing a platform that is truly reliable, fully compliant,
                and easy to use—so you can focus on trading, not troubleshooting.
              </p>
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-2 md:mb-3">Our Vision</h2>
              <p className="text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 leading-[1.55] md:leading-relaxed">
                To be the world’s most trusted crypto exchange for P2P and professional traders alike, enabling seamless global
                access to digital assets without compromise.
              </p>
            </div>
          </div>
        </section>

        {/* Key Features */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-2.5 md:mb-4">Key Features</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5 md:gap-6">
            {[
              { icon: "⚡", title: "Fast & Reliable Trading", text: "Our matching engine processes orders in under 100ms, so you never miss an opportunity." },
              { icon: "🔄", title: "Quick Trade", text: "Instantly swap between BTC, ETH, and USDT on major chains without complex order entry." },
              { icon: "💼", title: "Multi-Chain Wallets", text: "Unique deposit addresses on Bitcoin, Ethereum, BSC, Tron, and USDT networks—HD-wallet secured." },
              { icon: "📊", title: "Real-Time Charts & Data", text: "Integrated live charts and order books for informed decision-making." },
              { icon: "💬", title: "24/7 Support", text: "Our team is available via email and in-app chat to help you trade confidently." },
            ].map((f) => (
              <div key={f.title} className="flex items-start gap-2.5 md:gap-4">
                <div className="text-xl sm:text-2xl md:text-3xl text-blue-600 dark:text-blue-300 leading-none">{f.icon}</div>
                <div>
                  <p className="font-semibold text-blue-900 dark:text-white text-[13px] sm:text-sm md:text-base">{f.title}</p>
                  <p className="text-[12.5px] sm:text-sm md:text-base text-gray-700 dark:text-gray-300">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Security & Compliance */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-2.5 md:mb-4">
            Security & Compliance
          </h2>
          <ul className="list-disc list-inside text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 space-y-1.5 md:space-y-2">
            <li>
              <strong className="text-blue-900 dark:text-white">HD Wallet Derivation:</strong> Addresses derived via BIP-44/84; private keys never leave our secure vault.
            </li>
            <li>
              <strong className="text-blue-900 dark:text-white">Data Encryption:</strong> All traffic secured by TLS 1.3; sensitive data encrypted at rest with AES-256.
            </li>
            <li>
              <strong className="text-blue-900 dark:text-white">Two-Factor Authentication:</strong> Optional 2FA for an extra layer of account protection.
            </li>
            <li>
              <strong className="text-blue-900 dark:text-white">KYC/AML:</strong> Fully compliant with global regulations, so you trade with peace of mind.
            </li>
          </ul>
        </section>

        {/* Team */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-3.5 md:mb-6">Meet the Team</h2>
          <p className="text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 leading-[1.55] md:leading-relaxed mb-5 md:mb-8">
            Our founders and core team combine over a decade of trading and blockchain expertise—dedicated to building a platform that solves the real problems traders face.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-8">
            {[
              { name: "Anubhav B.", role: "Co-Founder & CEO" },
              { name: "Priya S.",    role: "CTO & Security Lead" },
              { name: "Rohit S.",    role: "Head of Compliance" },
              { name: "Neha P.",     role: "Community Manager" },
            ].map((m) => (
              <div
                key={m.name}
                className="bg-blue-50 dark:bg-gray-900 p-4 md:p-6 rounded-xl md:rounded-2xl shadow-sm border border-blue-100 dark:border-gray-800 text-center"
              >
                <div className="h-16 w-16 md:h-24 md:w-24 mx-auto mb-2.5 md:mb-4 bg-white dark:bg-black rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300 text-xl md:text-3xl font-bold shadow">
                  {m.name.split(" ").map((n) => n[0]).join("")}
                </div>
                <h3 className="text-sm md:text-xl font-semibold text-blue-800 dark:text-white">{m.name}</h3>
                <p className="text-xs md:text-base text-gray-600 dark:text-gray-300">{m.role}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Facts */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-4 md:p-6 border-t-4 border-blue-500 dark:border-blue-700/70">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-6 text-center">
            {[
              { k: "100ms", v: "Order Engine" },
              { k: "24/7",  v: "Support" },
              { k: "Multi-Chain", v: "BTC • ETH • BSC • TRON" },
              { k: "KYC/AML", v: "Compliant" },
            ].map((x) => (
              <div key={x.k} className="bg-blue-50 dark:bg-gray-900 rounded-lg border border-blue-100 dark:border-gray-800 p-3 md:p-4">
                <div className="text-sm md:text-xl font-bold text-blue-700 dark:text-blue-300">{x.k}</div>
                <div className="text-[11px] sm:text-xs md:text-sm text-blue-700/80 dark:text-blue-300/80">{x.v}</div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="bg-white dark:bg-black rounded-xl md:rounded-2xl shadow-lg p-5 md:p-8 border-t-4 border-blue-500 dark:border-blue-700/70 text-center">
          <h2 className="text-xl sm:text-2xl md:text-3xl font-semibold text-blue-800 dark:text-white mb-1.5 md:mb-3">
            Ready to Trade?
          </h2>
          <p className="text-[13px] sm:text-sm md:text-base text-gray-700 dark:text-gray-200 mb-4 md:mb-6">
            Sign up in under a minute—no more banking delays, just fast, secure crypto access.
          </p>
          <button
            className="bg-blue-600 text-white px-5 py-2 md:px-8 md:py-3 rounded-lg hover:bg-blue-700 transition text-sm md:text-base"
            onClick={() => navigate("/signup")}
          >
            Create Your Account
          </button>
        </section>
      </main>
    </motion.div>
  );
}
